import { FileTextIcon } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui'
import { useToast } from '@/hooks'
import { useIsTrialAccount } from '@/modules/account'
import { UpgradeButton } from '@/modules/auth'
import { type Statement, useProjectMutations } from '@/modules/projects'
import { isStatementProcessing } from '@/modules/projects/status'
import { hasFullFileToRead } from '../../../trial'

const ReadFullFileButton = ({ statement }: { statement: Statement }) => {
  const { readFullFile } = useProjectMutations()
  const { successToast, errorToast } = useToast()
  const [isStarting, setIsStarting] = useState(false)

  const readFull = async () => {
    setIsStarting(true)
    try {
      await readFullFile(statement.id)
      successToast('Full file added', 'Process it to extract every page.')
    } catch (error) {
      errorToast('Failed to extract the full file', error as Error)
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <Button
      theme="outline"
      size="xs"
      className="gap-1.5"
      isLoading={isStarting}
      loadingText="Starting..."
      disabled={isStatementProcessing(statement)}
      onClick={readFull}
    >
      <FileTextIcon className="size-3.5" />
      Extract the full file
    </Button>
  )
}

// During the trial the unread pages wait for an upgrade; after it, the kept original can be read.
export const TrialUnreadAction = ({ statement }: { statement: Statement }) => {
  const isTrial = useIsTrialAccount()

  if (isTrial) {
    return (
      <UpgradeButton theme="outline" size="xs">
        Upgrade to extract it
      </UpgradeButton>
    )
  }
  if (!hasFullFileToRead(statement)) return null
  return <ReadFullFileButton statement={statement} />
}
